import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
} from '@mui/material';

import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import PageDialog from '../components/pages/PageDialog';
import DeleteDialog from '../components/pages/DeleteDialog';
import { PageType } from 'src/types/domain.types';
import { useGetPagesQuery } from '../api/pages/getPages';
import { toast } from 'react-toastify';
import { useCreatePageMutation } from '../api/pages/createPage';
import { useUpdatePageMutation } from '../api/pages/updatePage';
import { useDeletePageMutation } from '../api/pages/deletePage';

export default function PagesList() {
  const navigate = useNavigate();
  const [openPageDialog, setOpenPageDialog] = useState(false);
  const [showDeletePageDialog, setShowDeletePageDialog] = useState(false);

  const [editPage, setEditPage] = useState(null);
  const [deletePage, setDeletePage] = useState<number | null>(null);

  const { data: pages = [], isLoading, refetch } = useGetPagesQuery();

  const { mutateAsync: createPage } = useCreatePageMutation();
  const { mutateAsync: updatePage } = useUpdatePageMutation();
  const { mutateAsync: deletePageMutation } = useDeletePageMutation();

  const deletePageHandler = (id: number) => {
    deletePageMutation(id).then(() => {
      setDeletePage(null);
      refetch();
      toast.success('Page deleted');
    });
  };
  const successPageHandler = (page: PageType) => {
    if (page?.page_id) {
      updatePage(page).then(() => {
        refetch();
        toast.success('Page updated');
      });
    } else {
      createPage(page).then(() => {
        refetch();
        toast.success('Page created');
      });
    }
    setOpenPageDialog(false);
  };

  const closePageDialog = () => {
    setEditPage(null);
    setOpenPageDialog(false);
  };

  const openAddPageDialog = () => {
    setEditPage(null);
    setOpenPageDialog(true);
  };

  const openDeletePageDialog = (index: number) => {
    const page = pages[index];
    setDeletePage(page.page_id);
    setShowDeletePageDialog(true);
  };

  return (
    <>
      <Helmet>
        <title>Pages</title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Pages and Rules
          </Typography>
          <Button
            variant='contained'
            startIcon={<Iconify icon='eva:plus-fill' />}
            onClick={() => openAddPageDialog()}
          >
            New Page
          </Button>
        </Stack>

        <Card>
          {isLoading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Route</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {pages.map((page, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell component='th' scope='row'>
                          {page.page_key ?? ''}
                        </TableCell>

                        <TableCell component='th' scope='row'>
                          <Button
                            variant='outlined'
                            onClick={() =>
                              navigate(
                                `/dashboard/pages/edit?page_id=${page.page_id}`
                              )
                            }
                            startIcon={<Iconify icon='eva:edit-outline' />}
                            sx={{ mr: 2 }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant='outlined'
                            onClick={() => openDeletePageDialog(index)}
                            startIcon={<Iconify icon='eva:trash-2-outline' />}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>

      <PageDialog
        page={editPage}
        open={openPageDialog}
        onSuccessHandler={successPageHandler}
        onClose={closePageDialog}
      />

      <DeleteDialog
        openConfirm={showDeletePageDialog}
        setOpenConfirm={setShowDeletePageDialog}
        deleteHandler={deletePageHandler}
        id={Number(deletePage)}
        type='page'
      />
    </>
  );
}
